import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Coins, Search, Play, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const chf = (v) => (v == null ? "—" : Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function AdminMoneyIntegrity() {
  const [scan, setScan] = useState(null);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(null);

  const loadReports = () => apiClient.get("/admin/money-migration/reports?limit=5").then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { loadReports(); }, []);

  const dryRun = async () => {
    setBusy("scan");
    try {
      const r = await apiClient.post("/admin/money-migration/dry-run", null, { timeout: 300000 });
      setScan(r.data);
      toast[r.data.findings_total ? "warning" : "success"](`${r.data.scanned_docs} Dokumente geprüft · ${r.data.findings_total} nicht Rappen-exakte Beträge`);
    } catch (e) { toast.error(e.response?.data?.detail || "Prüfung fehlgeschlagen"); }
    finally { setBusy(null); }
  };

  const migrate = async () => {
    if (!window.confirm("Migration ausführen? Vorher wird ein Backup erstellt und per Restore-Test geprüft. Erst danach werden Beträge gerundet.")) return;
    setBusy("run");
    try {
      const r = await apiClient.post("/admin/money-migration/run", null, { timeout: 600000 });
      r.data.ok ? toast.success(`Migration abgeschlossen: ${r.data.changed} Beträge gerundet (${r.data.duration_s} s)`)
        : toast.error(r.data.error || "Migration nicht erfolgreich");
      setScan(null);
      loadReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Migration fehlgeschlagen"); }
    finally { setBusy(null); }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm mt-4" data-testid="admin-money-integrity-card">
      <div className="flex items-start gap-3 mb-3">
        <Coins className="w-5 h-5 text-amber-600 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900">Rappen-Integrität (Money-Migration)</h3>
          <p className="text-xs text-slate-500">Alle Geldbeträge in der Datenbank müssen auf den Rappen exakt sein. Der Scan läuft jede Nacht um 04:10; die Migration rundet Abweichungen (kaufmännisch) – nur nach erfolgreichem Backup + Restore-Test.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={dryRun} disabled={!!busy} data-testid="money-dry-run-btn">
            {busy === "scan" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} <span className="ml-1">Prüfen</span>
          </Button>
          <Button size="sm" onClick={migrate} disabled={!!busy} data-testid="money-migrate-btn">
            {busy === "run" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} <span className="ml-1">Migration ausführen</span>
          </Button>
        </div>
      </div>

      {scan && (
        <div className={`rounded-md border px-3 py-2 text-xs mb-3 ${scan.findings_total ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`} data-testid="money-scan-result">
          <div className="flex items-center gap-1.5 font-medium">
            {scan.findings_total ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {scan.scanned_docs} Dokumente geprüft · {scan.findings_total} nicht exakte Beträge · {scan.unbalanced_total} unausgeglichene Buchungen · {scan.duration_s} s
          </div>
          {scan.findings.slice(0, 8).map((f, i) => (
            <div key={i} className="font-mono text-[11px] mt-0.5">{f.collection} · {f._id.slice(0, 8)} · {f.path}: {f.value} → {chf(f.rappen)}</div>
          ))}
          {scan.unbalanced_entries.slice(0, 5).map((u, i) => (
            <div key={`u${i}`} className="text-[11px] mt-0.5">Buchung {u.date} «{u.description}»: Soll {chf(u.debit)} / Haben {chf(u.credit)} (Diff {chf(u.diff)})</div>
          ))}
        </div>
      )}

      {reports.length > 0 && (
        <div className="text-xs" data-testid="money-reports">
          <div className="font-medium text-slate-700 mb-1">Letzte Läufe</div>
          {reports.map((r) => (
            <div key={r.id} className="flex items-center gap-2 py-1 border-t border-slate-100" data-testid={`money-report-${r.id}`}>
              {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />}
              <span className="text-slate-600">{new Date(r.started_at).toLocaleString("de-CH")}</span>
              <span className="text-slate-500">{r.kind === "nightly_scan" ? "Nacht-Scan" : `Migration (${r.triggered_by})`}</span>
              <span className="ml-auto text-slate-700">
                {r.kind === "nightly_scan" ? `${r.findings_total} Befunde` : `${r.changed ?? 0} gerundet · Backup ${r.backup?.file || "—"} · Restore-Test ${r.restore_test?.ok ? "ok" : "fehlgeschlagen"}`}
                {r.unbalanced_total ? ` · ${r.unbalanced_total} unausgeglichen` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
